import * as R from 'ramda';

import TimeEntryStatusIcon from '@atom/components/common/timeEntryStatusIcon/TimeEntryStatusIcon';
import UserThumbnail from '@atom/components/common/UserThumbnail';
import { View } from '@atom/components/teamPortal/TeamContext';
import { SearchParamType, useNavigation } from '@atom/hooks/useNavigation';
import { useTeamNavigation } from '@atom/hooks/useTeamNavigation';
import { Checkbox, ListTable } from '@atom/mui';
import { TimeEntryStatus } from '@atom/types/timeEntry';
import { UserDetail } from '@atom/types/user';
import { toggleFromSet } from '@atom/utilities/setUtilities';
import { convertMillisToExactHours } from '@atom/utilities/timeUtilities';
import { getUserFullName } from '@atom/utilities/userUtilities';

import classes from './timeSheetList.module.css';

const { TableRow, TableCell } = ListTable;

const styles = {
  smallCell: {
    padding: '0.5rem',
  },
};

interface Props {
  user: UserDetail;
  selectedPayPeriodWeek: number;
  selectedUserIds: Set<string>;
  setSelectedUserIds: (selectedUserIds: Set<string>) => void;
}

const TimeSheetListTableRow = ({
  user,
  selectedPayPeriodWeek,
  selectedUserIds,
  setSelectedUserIds,
}: Props) => {
  const { teamNavigation } = useTeamNavigation();
  const { payPeriodWeeks } = teamNavigation;
  const [, { updateSearchParams }] = useNavigation({
    view: SearchParamType.STRING,
    timeSheetUserId: SearchParamType.STRING,
    timeSheetUserName: SearchParamType.STRING,
  });

  const handleClick = () => {
    updateSearchParams(
      {
        view: View.TIME_SHEET,
        timeSheetUserId: user.id,
        timeSheetUserName: getUserFullName(user),
      },
      false,
    );
  };

  const toggleCheck = () => {
    setSelectedUserIds(toggleFromSet(selectedUserIds, user.id));
  };

  const currentWeekDates = payPeriodWeeks[selectedPayPeriodWeek] || [];
  const hasNoTimeEntries = R.isEmpty(user?.timeEntries?.timeEntries);

  const disableCheckbox =
    hasNoTimeEntries || user?.timeEntries?.status !== TimeEntryStatus.pending;

  return (
    <>
      <TableRow key={user.id}>
        <TableCell padding="checkbox">
          <Checkbox
            checked={selectedUserIds.has(user.id)}
            onClick={toggleCheck}
            disabled={disableCheckbox}
          />
        </TableCell>
        <TableCell width="30px" center style={styles.smallCell}>
          <UserThumbnail
            image={user.photoUrl}
            alt={`${user.firstName} ${user.lastName}`}
          />
        </TableCell>
        <TableCell>
          <div className={classes.nameCell} onClick={handleClick}>
            {getUserFullName(user)}
          </div>
        </TableCell>
        <TableCell>
          {hasNoTimeEntries ? '-' : user?.timeEntries?.totalOpenWorkOrders}
        </TableCell>
        <TableCell>
          {hasNoTimeEntries ? '-' : user?.timeEntries?.totalWorkTypes}
        </TableCell>
        <TableCell>
          {hasNoTimeEntries ? '-' : user?.timeEntries?.totalWorkOrders}
        </TableCell>
        <TableCell width="30px" center style={styles.smallCell} />
        {currentWeekDates.map(day => {
          const dailyTotal = R.pathOr(
            [],
            ['timeEntries', 'dailyTotals'],
            user,
          ).find(item => item.date === day);

          const hours =
            dailyTotal && dailyTotal?.duration !== 0
              ? convertMillisToExactHours(dailyTotal?.duration)
              : '-';

          return (
            <TableCell width="30px" center style={styles.smallCell} key={day}>
              {hours}
            </TableCell>
          );
        })}
        <TableCell width="30px" center style={styles.smallCell} />
        <TableCell center style={styles.smallCell}>
          {hasNoTimeEntries || !user?.timeEntries?.totalDuration
            ? '-'
            : convertMillisToExactHours(user?.timeEntries?.totalDuration)}
        </TableCell>
        <TableCell center style={styles.smallCell}>
          {user?.timeEntriesTotalDuration
            ? convertMillisToExactHours(user?.timeEntriesTotalDuration)
            : '-'}
        </TableCell>
        <TableCell>
          {hasNoTimeEntries ? (
            <div className={classes.emptyTitle}>Not Started</div>
          ) : (
            <div className={classes.statusContainer}>
              <TimeEntryStatusIcon status={user?.timeEntries?.status} />
              <div className={classes.statusTitle}>
                {user?.timeEntries?.status}
              </div>
            </div>
          )}
        </TableCell>
      </TableRow>
    </>
  );
};

export default TimeSheetListTableRow;
